// src/pages/PublicRecipeView.tsx
import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import RecipeTextSections from '../components/recipes/RecipeTextSections';
import RecipeTitleBlock from '../components/recipes/RecipeTitleBlock';
import { Box, Card, CardBody, Image, Page, PageStatus, PlainButton, Text } from '../components/ui';
import useDismissibleOverlay from '../hooks/ui/useDismissibleOverlay';
import useRecipeDetails from '../hooks/recipes/useRecipeDetails';
import useRecipeImageRealtime from '../hooks/recipes/useRecipeImageRealtime';

const PublicRecipeView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { recipe, loading, error } = useRecipeDetails(id);
  const [isImageOverlayOpen, setIsImageOverlayOpen] = useState(false);
  const currentImageUrl = useRecipeImageRealtime(id, recipe?.image || null);

  const closeImageOverlay = useCallback(() => {
    setIsImageOverlayOpen(false);
  }, []);

  useDismissibleOverlay({
    isOpen: isImageOverlayOpen,
    onClose: closeImageOverlay,
    disablePullToRefresh: true,
  });

  if (loading) {
    return <PageStatus marginTop={4} centered>Chargement de la recette...</PageStatus>;
  }

  if (error) {
    return <PageStatus marginTop={4} alertVariant="danger">{error}</PageStatus>;
  }

  if (!recipe) {
    return <PageStatus marginTop={4} alertVariant="warning">Recette introuvable.</PageStatus>;
  }

  const imageWidth = 150; // Largeur de l'image
  const imageRightMargin = 20; // Marge droite de l'image
  // Removed unused variables cardBodyPaddingLeft and titleLeftPosition

  return (
    <Page marginTop={4} paddingBottom={5}>
      <Card marginBottom={4} shadow="sm" style={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: `${imageWidth}px`, // Taille de l'image
            height: '150px',
            overflow: 'hidden',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)', // Ombre
            transform: 'rotate(5deg)', // Légère rotation
            zIndex: 1, // Pour s'assurer qu'elle est au-dessus du contenu
            backgroundColor: '#fff', // Fond blanc pour l'image
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px', // Petit padding pour l'effet "cadre"
          }}
        >
          <PlainButton
            className="edit-recipe-image-button"
            onClick={() => setIsImageOverlayOpen(true)}
            aria-label="Voir la photo en plein ecran"
          >
            <Image
              src={currentImageUrl || `${process.env.PUBLIC_URL}/marmite.png`}
              alt={recipe.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '4px', // Rayon de la bordure interne de l'image
              }}
            />
          </PlainButton>
        </Box>
        {isImageOverlayOpen && (
          <Box
            className="edit-recipe-image-overlay"
            data-testid="public-recipe-image-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={`Photo de ${recipe.title}`}
          >
            <PlainButton
              className="edit-recipe-image-backdrop"
              aria-label="Fermer la photo en cliquant hors image"
              onClick={closeImageOverlay}
            />
            <Box className="edit-recipe-image-frame">
              <PlainButton
                className="edit-recipe-image-close-button"
                aria-label="Fermer la photo"
                onClick={closeImageOverlay}
              >
                x
              </PlainButton>
              <Image
                src={currentImageUrl || `${process.env.PUBLIC_URL}/marmite.png`}
                className="edit-recipe-image-fullscreen"
                alt={recipe.title}
              />
            </Box>
          </Box>
        )}
        <CardBody
          style={{
            paddingTop: '20px', // Garde le padding top
            position: 'relative', // Peut être retiré si plus rien n'est absolument positionné ici
          }}
        >
          <RecipeTitleBlock
            recipe={recipe}
            cardTitle
            titleLayout={{ marginTop: 0, marginBottom: 2 }}
            titleStyle={{
              fontSize: '2rem',
              width: recipe.image ? `calc(100% - ${imageWidth}px - ${imageRightMargin}px)` : '100%',
            }}
            renderAuthor={(recipeWithAuthor) => (
              recipeWithAuthor.authors ? (
                <Text tone="muted" marginBottom={2}>
                  Par {recipeWithAuthor.authors.first_name} {recipeWithAuthor.authors.name}
                </Text>
              ) : null
            )}
          />
          {/* Removed marginTop: '150px' as content flows naturally now */}
          <RecipeTextSections recipe={recipe} />
        </CardBody>
      </Card>
    </Page>
  );
};

export default PublicRecipeView;
